import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from "react-router-dom";
import { getLanguage } from '@utils/LanguageUtils';

import { IContentTag, IGetArticlesApiParam, ITagPayload } from '@common/domain/models/Article';

import { translate } from '@translations/Translater';

import SearchTextInput from '@components/atoms/Input/SearchTextInput';
import Shimmer from '@components/atoms/Shimmer';
import { Card } from '@components/Card';
import MultipleSlider from '@components/Slider/MultipleSlider';
import Text, { FontFamilyVariant, TextType } from '@components/Text';

import Navigator from '@navigator/index';
import { NavigationUrl, URLs } from '@navigator/NavigationUrl';

import { ChannelName, CLIENT_NAME, ContentKey } from '@constants/config';

import { libraryMessages } from '@containers/Library/messages';

import ContentSection from '@presentation/Content/components/ContentSection';
import ContentSectionContainer from '@presentation/Content/components/ContentSectionContainer';
import GroupCard from '@presentation/Content/components/GroupCard';
import { ContentType, groupSetting, TagType } from '@presentation/Content/config';

import { Routes } from '@navigator/Routes';
import './styles.less';

interface IProps {
    fetchTags: (payload: ITagPayload) => void;
    tags: IContentTag[],
    tagsLoader: boolean,
    countMap: {},
    getDashboardContentList?: (params: IGetArticlesApiParam) => void;
    contentTypeMap?: {};
}

const LibraryHomeView = (props: IProps) => {
    const { t } = useTranslation();
    const { fetchTags, tags, tagsLoader, countMap } = props;
    const [searchTerm, setSearchTerm] = useState("");
    const [groupRandomSet, setGroupRandomSet] = useState<number[]>([]);
    const [techRandomSet, setTechRandomSet] = useState<number[]>([]);

    const isGuestPage = useLocation()?.pathname?.includes(Routes.Auth.guest);

    useEffect(function onMount() {
        fetchTags({ products: CLIENT_NAME, scopes: ChannelName.CONTENT, keys: `${ContentKey.SECTOR},${ContentKey.TECHNOLOGY}`, languageCodes: getLanguage() });
        const nums = new Set();
        while (nums.size !== 12) {
            nums.add(Math.floor(Math.random() * 12) + 1);
        }
        setGroupRandomSet(Array.from(nums) as number[])
    }, []);

    useEffect(function createTechRandomSet() {
        const nums = new Set();
        while (nums.size !== 12) {
            nums.add(Math.floor(Math.random() * 12) + 1);
        }
        setTechRandomSet(Array.from(nums) as number[])
    }, [])


    const getGroupContent = () => {
        const filteredTags = tags?.filter(tag => countMap[tag.groupName] > 0 && tag.key === ContentKey.SECTOR);
        const onClickGroupCard = (tag: IContentTag) => {
            Navigator.push(NavigationUrl.generate(URLs.contentListingPage,
                {
                    content: {
                        key: tag.key,
                        [tag.isGrouped ? TagType.GROUP : TagType.VALUE]: tag.groupName,

                    }
                }));
        }

        return (
            <MultipleSlider customSetting={groupSetting}>
                {filteredTags?.concat([{}])?.map((tag, index) => {
                    return <GroupCard
                        key={tag.groupName}
                        title={tag.groupName}
                        description={`${countMap[tag.groupName]} ${translate(t, libraryMessages.articlesAndVideos)}`}
                        onClickGroup={() => onClickGroupCard(tag)}
                        index={index}
                        randomSetValues={groupRandomSet}
                    />
                })}
            </MultipleSlider>
        )
    }

    const getBrowseByTech = () => {
        const filteredTags = tags?.filter(tag => countMap[tag.groupName] > 0 && tag.key === ContentKey.TECHNOLOGY);
        const onClickGroupCard = (tag: IContentTag) => {
            Navigator.push(NavigationUrl.generate(URLs.contentListingPage,
                {
                    content: {
                        key: tag.key,
                        [tag.isGrouped ? TagType.GROUP : TagType.VALUE]: tag.groupName,

                    }
                }));
        }

        return (
            <MultipleSlider customSetting={groupSetting}>
                {filteredTags?.concat([{}])?.map((tag, index) => {
                    return <GroupCard
                        key={tag.groupName}
                        title={tag.groupName}
                        description={`${countMap[tag.groupName]} ${translate(t, libraryMessages.articlesAndVideos)}`}
                        onClickGroup={() => onClickGroupCard(tag)}
                        index={index}
                        randomSetValues={techRandomSet}
                    />
                })}
            </MultipleSlider>
        )
    }

    if (tagsLoader) {
        return Array.apply(null, Array(5)).map((index: number) => {
            return <Shimmer key={index} />
        });
    }

    const onSearch = (search) => {
        setSearchTerm(search);
        if (search?.length) {
            Navigator.push(NavigationUrl.generate(URLs.contentSearchPage, {
                content: {
                    group: ContentType.all,
                    key: ContentType.all,
                    searchTerm: search
                }
            }));
        }
    }

    const onClickLibrary = () => {
        if (isGuestPage) {
            Navigator.push(NavigationUrl.generate(URLs.library));
        }
    }

    return (
        <div className="library_home_container">
            <Card className="library_card_container">
                <div className="library_heading">
                    <Text
                        className="heading_text"
                        textType={TextType.h1}
                        fontFamilyVariant={FontFamilyVariant.bold}
                        text={translate(t, libraryMessages.digitalLibrary)}
                    />

                    {!isGuestPage && <SearchTextInput
                        onSearchKeywordChange={onSearch}
                        onEnterPressed={onSearch}
                        searchKeyword={searchTerm}
                        className="search_article_input"
                        placeholder={translate(t, libraryMessages.searchPlaceholder)}
                    />}
                </div>
                <span className="discover">{t("content.labelsAndTitles.LIBRARY_DESCRIPTION")}<span onClick={onClickLibrary} className={isGuestPage && `discover_library`}>{t("content.labelsAndTitles.LIBRARY")}</span>.</span>
                <ContentSectionContainer groupName={ContentType.all} heading={translate(t, libraryMessages.latestArticles)} tagType={ContentType.all} />
                <ContentSection heading={translate(t, libraryMessages.lookingFor)} children={getGroupContent()} />
                <ContentSection heading={translate(t, libraryMessages.browseByTech)} children={getBrowseByTech()} />
                {tags && tags.map((tag: IContentTag) => {
                    return <ContentSectionContainer groupName={tag.groupName} heading={tag.groupName} groupValues={tag.groupValues} tagType={tag.key} isGrouped={tag.isGrouped} isGuestPage={isGuestPage} />
                })}
            </Card>
        </div>
    )
}

export default LibraryHomeView;